<template>
  <div class="order-details">
    <LoadingPulse
      :loading="loading"
      class="loading-spinner"
    />
    <div
      v-if="!loading && order"
      class="order-details-content"
    >
      <div class="order-details-header">
        <div class="title-text">
          {{ titleText }}
        </div>
        <div class="actions">
          <LockingButton
            class="duplicate"
            :locked="creatingQuote"
            :text="'Duplicate'"
            @click="onDuplicateClicked"
          />
          <LockingButton
            class="save"
            :disabled="!isDirty"
            :locked="saving"
            :text="'Save'"
            @click="onSaveOrderClicked"
          />
        </div>
      </div>
      <div class="left-column">
        <div class="card">
          <div class="header">
            General Information
          </div>
          <div class="content">
            <Dropdown
              v-model="order.buildTypeId"
              label="Build Type"
              :show-inactive="order.id > 0"
              :show-required="true"
              :options="buildTypeDropdownOptions"
              class="input"
            />
            <CartridgeSearchMachine
              v-if="cartridgeRequired"
              v-model="order.cartridgeId"
              :show-clear="true"
              :show-required="true"
              class="input"
            />
            <label class="input-label">Scheduled Date</label>
            <DatePicker
              id="scheduled-date"
              v-model="scheduledDate"
              class="input date-picker"
              input-format="MM-dd-yyyy"
              :week-starts-on="0"
              @update:modelValue="onScheduledDateChanged"
            />
            <Checkbox
              v-model="order.onHold"
              class="input"
              label="On Hold"
            />
            <NumericInput
              v-model="order.partsDiscountPercentage"
              label="Parts Discount %"
              class="input"
            />
            <NumericInput
              v-model="order.servicesDiscountPercentage"
              label="Services Discount %"
              class="input"
            />
            <label class="input-label">
              Total
            </label>
            <div class="total">
              {{ $methods.formatPrice(order.total) }}
            </div>
            <AuditInformation
              v-if="order.createdByUser && order.createdDate && order.lastModifiedByUser && order.lastModifiedDate"
              :created-by-user="order.createdByUser"
              :created-date="order.createdDate"
              :last-modified-by-user="order.lastModifiedByUser"
              :last-modified-date="order.lastModifiedDate"
            />
            <div class="approval-info">
              Approved by {{ order.approvedByUser.firstName }} {{ order.approvedByUser.lastName }} {{ $methods.formatDate(order.approvedDate) }}
            </div>
          </div>
        </div>
        <div class="card">
          <div class="header">
            Customer Information
          </div>
          <div class="content">
            <div class="customer-tile">
              <div class="customer-tile-content">
                <div
                  v-if="order.customer.firstName || order.customer.lastName"
                  class="name"
                >
                  {{ order.customer.firstName }} {{ order.customer.lastName }}
                </div>
                <div
                  v-if="
                    order.customer.addressLine1 ||
                      order.customer.addressLine2 ||
                      order.customer.city ||
                      stateDisplayValue ||
                      order.customer.postalCode
                  "
                  class="address"
                >
                  <div v-if="order.customer.addressLine1">
                    {{ order.customer.addressLine1 }}
                  </div>
                  <div v-if="order.customer.addressLine2">
                    {{ order.customer.addressLine2 }}
                  </div>
                  <div
                    v-if="
                      order.customer.city ||
                        stateDisplayValue ||
                        order.customer.postalCode
                    "
                  >
                    <span
                      v-if="order.customer.city"
                    >{{ order.customer.city }}, </span><span v-if="stateDisplayValue">{{
                      stateDisplayValue + " "
                    }}</span><span v-if="order.customer.postalCode">{{
                      order.customer.postalCode
                    }}</span>
                  </div>
                </div>
                <a
                  v-if="order.customer.phoneNumber"
                  :href="'tel:' + order.customer.phoneNumber"
                >
                  {{ $methods.formatPhoneNumber(order.customer.phoneNumber) }}
                </a>
                <a
                  v-if="order.customer.email"
                  :href="'mailto:' + order.customer.email"
                >
                  {{ order.customer.email }}
                </a>
              </div>
              <DotsSvg @click.stop="showMenu = true" />
              <div
                v-if="showMenu"
                class="menu"
              >
                <div v-click-outside="closeMenu">
                  <div
                    class="menu-option"
                    @click="onEditCustomerClicked"
                  >
                    Edit Customer
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="header">
            Quickbooks Invoice
          </div>
          <div class="content flexed">
            <LockingButton
              v-if="order.quickbooksInvoiceUrl === ''"
              :locked="creatingInvoice"
              :text="'Create Invoice'"
              @click="onCreateInvoiceClicked"
            />
            <a
              v-if="order.quickbooksInvoiceUrl !== ''"
              aria-label="View Quickbooks Order Page (opens in a new window)"
              class="view-invoice"
              :href="order.quickbooksInvoiceUrl"
              target="_blank"
            >
              View Invoice
            </a>
            <LockingButton
              v-if="order.quickbooksInvoiceUrl !== ''"
              :locked="updatingInvoice"
              :text="'Update Invoice'"
              @click="onUpdateInvoiceClicked"
            />
          </div>
        </div>
        <div class="card">
          <div class="header">
            Notes
          </div>
          <div class="content">
            <TextArea
              v-model="newNoteText"
              :show-required="true"
            />
            <LockingButton
              class="add"
              :disabled="!addNoteEnabled"
              :locked="addingNote"
              :text="'Add'"
              @click="onAddNoteClicked"
            />
            <NoteTile
              v-for="note in order.notes"
              :key="note.id"
              :note="note"
              @delete="onDeleteNote"
              @edit="onEditNote"
            />
          </div>
        </div>
        <div class="card">
          <div class="header">
            Payments
          </div>
          <div class="content">
            <CurrencyInput
              v-model="newPaymentAmount"
              :show-required="true"
            />
            <LockingButton
              class="add"
              :disabled="!addPaymentEnabled"
              :locked="addingPayment"
              :text="'Add'"
              @click="onAddPaymentClicked"
            />
            <div
              v-for="payment in order.orderPayments"
              :key="payment.amount"
              class="payment"
            >
              <div class="price">
                {{ $methods.formatPrice(payment.amount) }}
              </div>
              <div class="received-by">
                Received by {{ payment.receivedByUser.firstName }} {{ payment.receivedByUser.lastName }} {{ $methods.formatDate(payment.receivedDate) }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="right-column">
        <div class="card">
          <div class="header">
            Build Steps
          </div>
          <div class="content">
            <div class="overflow-wrapper">
              <table>
                <thead>
                  <tr>
                    <th class="no-sort">
                      <div>
                        PROCESS NAME
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        PROCESS STATUS
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        PROCESS STATE
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="buildStep in order.orderBuildSteps"
                    :key="buildStep.id"
                  >
                    <td>
                      {{ buildStep.processName }}
                    </td>
                    <td>
                      {{ statusText(buildStep) }}
                    </td>
                    <td>
                      <button
                        v-if="buildStep.processState === 1"
                        class="complete"
                        @click.stop="onOrderBuildStepStatusChanged(buildStep.id, 2)"
                      >
                        Start
                      </button>
                      <div v-if="buildStep.processState === 2">
                        Incomplete
                      </div>
                      <button
                        v-if="buildStep.processState === 3"
                        class="complete"
                        @click.stop="onOrderBuildStepStatusChanged(buildStep.id, 3)"
                      >
                        Complete
                      </button>
                      <button
                        v-if="buildStep.processState === 4"
                        class="complete"
                        @click.stop="onOrderBuildStepStatusChanged(buildStep.id, 2)"
                      >
                        Rollback
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="header">
            Customer Supplied Parts
          </div>
          <div class="content">
            <TextArea v-model="order.customerSuppliedParts" />
          </div>
        </div>
        <div class="card">
          <div class="header">
            Parts
            <PlusSvg
              class="add"
              @click="onAddPartClicked"
            />
          </div>
          <div class="content">
            <div class="overflow-wrapper">
              <table>
                <thead>
                  <tr>
                    <th class="no-sort drag-and-drop">
                      <div />
                    </th>
                    <th class="no-sort">
                      <div>
                        Item
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Status
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Price
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Action
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        P.O.
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Notes
                      </div>
                    </th>
                    <th class="no-sort" />
                  </tr>
                </thead>
                <draggable
                  v-if="order.orderParts.length > 0"
                  tag="tbody"
                  :list="order.orderParts"
                  item-key="id"
                  @change="onOrderPartsDisplayOrderChanged"
                >
                  <template #item="{element}">
                    <tr :class="element.active ? '' : 'inactive'">
                      <td> <DragAndDropSvg /> </td>
                      <td>{{ element.name }}</td>
                      <td>{{ orderPartStatusToDisplayValue(element.status) }}</td>
                      <td>
                        <CurrencyInput
                          v-model="element.price"
                          @blur="onOrderPartChanged(element.id, undefined, element.price, undefined)"
                        />
                      </td>
                      <td>
                        <div
                          v-if="element.showAllocateFromPurchaseOrder"
                          class="clickable"
                        >
                          <a
                            v-for="purchaseOrderAllocationOption in element.purchaseOrderAllocationOptions"
                            :key="purchaseOrderAllocationOption.purchaseOrderId"
                            @click="onOrderPartChanged(element.id, 1, element.price, purchaseOrderAllocationOption.purchaseOrderItemIds[0])"
                          >
                            {{ 'Allocate from P.O. ' + purchaseOrderAllocationOption.purchaseOrderNumber + ' - ' + purchaseOrderAllocationOption.purchaseOrderItemIds.length + ' available' }}
                          </a>
                        </div>
                        <div
                          v-if="element.showCreatePurchaseOrderForAllItems"
                          class="clickable"
                        >
                          <router-link
                            :to="{path: '/purchase-order/0', query: { customerId: order.customerId, orderPartName: encodeURIComponent(element.name), orderPartPrice: element.price, orderId: id, orderPartIds: orderPartIdsFromOrderPart(element) }}"
                            aria-label="Create New Purchase Order (opens in a new window)"
                            target="_blank"
                          >
                            {{ 'Create P.O. for all ' + element.name + ' on this order' }}
                          </router-link>
                        </div>
                        <div
                          v-if="element.showCreatePurchaseOrderForSingleItem"
                          class="clickable"
                        >
                          <router-link
                            :to="{path: '/purchase-order/0', query: { customerId: order.customerId, orderPartName: encodeURIComponent(element.name), orderPartPrice: element.price, orderId: id, orderPartIds: [element.id] }}"
                            aria-label="Create New Purchase Order (opens in a new window)"
                            target="_blank"
                          >
                            {{ 'Create P.O. for ' + element.name }}
                          </router-link>
                        </div>
                        <div
                          v-if="element.showPullFromInventory"
                          class="clickable"
                        >
                          <a @click="onOrderPartChanged(element.id, 2, element.price, undefined)">
                            {{ 'Pull from inventory - ' + element.totalQuantityOnHand + ' available' }}
                          </a>
                        </div>
                      </td>
                      <td>
                        <router-link
                          v-if="element.purchaseOrder"
                          aria-label="View Purchase Order (opens in a new window)"
                          :to="'/purchase-order/' + element.purchaseOrder.id"
                          target="_blank"
                        >
                          {{ element.purchaseOrder.poNumber }}
                        </router-link>
                      </td>
                      <td class="notes">
                        <NoteTile
                          v-for="note in element.notes"
                          :key="note.id"
                          :note="note"
                          @delete="onDeleteNote"
                          @edit="onEditOrderPartNote"
                        />
                      </td>
                      <td class="delete">
                        <CommentSvg
                          class="add-comment"
                          @click.stop="onAddOrderPartNoteClicked(element.id)"
                        />
                        <TrashCanSvg @click.stop="onRemoveOrderPartClicked(element.id)" />
                      </td>
                    </tr>
                  </template>
                </draggable>
                <tbody v-else>
                  <tr>
                    <td
                      :colspan="8"
                      class="message"
                    >
                      There are no parts on the order. Please click the '+' above to add a part.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="footer">
            Total: {{ $methods.formatPrice(order.partsTotal) }}
          </div>
        </div>
        <div class="card">
          <div class="header">
            Services
            <PlusSvg
              class="add"
              @click="onAddServiceClicked"
            />
          </div>
          <div class="content">
            <div class="overflow-wrapper">
              <table>
                <thead>
                  <tr>
                    <th class="no-sort drag-and-drop">
                      <div />
                    </th>
                    <th class="no-sort">
                      <div>
                        Name
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Description
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Price
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Fields
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Notes
                      </div>
                    </th>
                    <th class="no-sort" />
                    <th class="no-sort" />
                  </tr>
                </thead>
                <draggable
                  v-if="order.orderServices.length > 0"
                  tag="tbody"
                  :list="order.orderServices"
                  item-key="id"
                  @change="onOrderServicesDisplayOrderChanged"
                >
                  <template #item="{element}">
                    <tr :class="element.active ? '' : 'inactive'">
                      <td> <DragAndDropSvg /> </td>
                      <td>{{ element.name }}</td>
                      <td class="description">
                        {{ element.description }}
                      </td>
                      <td>
                        <CurrencyInput
                          v-model="element.price"
                          @blur="onOrderServiceChanged(element)"
                        />
                      </td>
                      <td class="services">
                        <div
                          v-for="(serviceFieldValue, index) in element.serviceFieldValues"
                          :key="index"
                          class="service"
                        >
                          <TextInput
                            v-model="serviceFieldValue.value"
                            :label="serviceFieldValue.name"
                            class="input"
                            :disabled="true"
                          />
                        </div>
                      </td>
                      <td class="notes">
                        <NoteTile
                          v-for="note in element.notes"
                          :key="note.id"
                          :note="note"
                          @delete="onDeleteNote"
                          @edit="onEditOrderServiceNote"
                        />
                      </td>
                      <td>
                        <Checkbox
                          v-model="element.completed"
                          class="input"
                          label="Completed?"
                          @update:modelValue="onOrderServiceChanged(element)"
                        />
                      </td>
                      <td class="delete">
                        <CommentSvg
                          class="add-comment"
                          @click.stop="onAddServiceNoteClicked(element.id)"
                        />
                        <TrashCanSvg @click.stop="onRemoveOrderServiceClicked(element.id)" />
                      </td>
                    </tr>
                  </template>
                </draggable>
                <tbody v-else>
                  <tr>
                    <td
                      :colspan="8"
                      class="message"
                    >
                      There are no services on the order. Please click the '+' above to add a service.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="footer">
            Total: {{ $methods.formatPrice(order.servicesTotal) }}
          </div>
        </div>
      </div>
    </div>
  </div>
  <transition name="slide-up">
    <NoteModal 
      v-if="showNoteModal && noteToEdit"
      :note="noteToEdit"
      :order-id="id"
      @note-changed="getOrder"
      @close="showNoteModal = false"
    />
  </transition>
  <transition name="slide-up">
    <NoteModal 
      v-if="showOrderServiceNoteModal && orderServiceNoteToEdit"
      :note="orderServiceNoteToEdit"
      :order-service-id="orderServiceId"
      :show-customer-visible-option="true"
      @note-changed="getOrder"
      @close="showOrderServiceNoteModal = false"
    />
  </transition>
  <transition name="slide-up">
    <NoteModal 
      v-if="showOrderPartNoteModal && orderPartNoteToEdit"
      :note="orderPartNoteToEdit"
      :order-part-id="orderPartId"
      :show-customer-visible-option="true"
      @note-changed="getOrder"
      @close="showOrderPartNoteModal = false"
    />
  </transition>
  <transition name="slide-up">
    <AddPartModal 
      v-if="showAddPartModal"
      :order-id="id"
      @part-added="getOrder"
      @close="showAddPartModal = false"
    />
  </transition>
  <transition name="slide-up">
    <AddServiceModal 
      v-if="showAddServiceModal"
      :order-id="id"
      @service-added="getOrder"
      @close="showAddServiceModal = false"
    />
  </transition>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import DatePicker from 'vue3-datepicker'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import AuditInformation from '@/components/elements/AuditInformation.vue'
import { BuildTypeService } from '@/services/buildType.service'
import NoteTile from '@/components/elements/NoteTile.vue'
import { Note } from '@/@types/responses/note/Note'
import NoteModal from '@/components/modals/NoteModal.vue'
import DotsSvg from '@/components/svgs/DotsSvg.vue'
import TextArea from '@/components/shared/TextArea.vue'
import CurrencyInput from '@/components/shared/CurrencyInput.vue'
import { NoteService } from '@/services/note.service'
import CartridgeSearchMachine from '@/components/elements/CartridgeSearchMachine.vue'
import { OrderService } from '@/services/order.service'
import { OrderDetails } from '@/@types/responses/order/OrderDetails'
import { OrderBuildStepDetails } from '@/@types/responses/order/OrderBuildStepDetails'
import Checkbox from '@/components/shared/Checkbox.vue'
import { OrderServiceDetails } from '@/@types/responses/order/OrderServiceDetails'
import AddPartModal from '@/components/modals/AddPartModal.vue'
import AddServiceModal from '@/components/modals/AddServiceModal.vue'
import Dropdown from '@/components/shared/Dropdown.vue'
import PlusSvg from '@/components/svgs/PlusSvg.vue'
import TrashCanSvg from '@/components/svgs/TrashCanSvg.vue'
import CommentSvg from '@/components/svgs/CommentSvg.vue'
import { BuildTypeDropdownOption } from '@/@types/responses/build_type/BuildTypeDropdownOption'
import { OrderPartService } from '@/services/orderPart.service'
import { OrderServiceService } from '@/services/orderService.service'
import { NoteMessage } from '@/@types/messages/NoteMessage'
import { OrderBuildStepService } from '@/services/orderBuildStep.service'
import { UpdateOrderMessage } from '@/@types/messages/UpdateOrderMessage'
import { UpdateOrderServiceMessage } from '@/@types/messages/UpdateOrderServiceMessage'
import { UpdateOrderBuildStepMessage } from '@/@types/messages/UpdateOrderBuildStepMessage'
import { UpdateOrderPartMessage } from '@/@types/messages/UpdateOrderPartMessage'
import { OrderPartDetails } from '@/@types/responses/order/OrderPartDetails'
import LockingButton from '@/components/shared/LockingButton.vue'
import moment from 'moment'
import { OrderPaymentMessage } from '@/@types/messages/OrderPaymentMessage'
import TextInput from '@/components/shared/TextInput.vue'
import NumericInput from '@/components/shared/NumericInput.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'
import DragAndDropSvg from '@/components/svgs/DragAndDropSvg.vue'
import draggable from 'vuedraggable'
import { UpdateOrderPartsMessage } from '@/@types/messages/UpdateOrderPartsMessage'
import { UpdateOrderServicesMessage } from '@/@types/messages/UpdateOrderServicesMessage'
import { StateService } from '@/services/states.service'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'

export default defineComponent({
  name: 'Order',
  components: {
    AddPartModal,
    AddServiceModal,
    AuditInformation,
    CartridgeSearchMachine,
    Checkbox,
    CommentSvg,
    CurrencyInput,
    DatePicker,
    DotsSvg,
    DragAndDropSvg,
    draggable,
    Dropdown,
    LoadingPulse,
    LockingButton,
    NoteModal,
    NoteTile,
    NumericInput,
    PlusSvg,
    TextArea,
    TextInput,
    TrashCanSvg
  },
  beforeRouteLeave() {
    if (this.showAddPartModal || this.showAddServiceModal || this.showNoteModal || this.showOrderPartNoteModal || this.showOrderServiceNoteModal) {
      this.showAddPartModal = false
      this.showAddServiceModal = false
      this.showNoteModal = false
      this.showOrderPartNoteModal = false
      this.showOrderServiceNoteModal = false

      return false
    }

    return true
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const buildTypeService = inject(PROVIDER_CONSTANTS.buildTypeService) as BuildTypeService
    const noteService = inject(PROVIDER_CONSTANTS.noteService) as NoteService
    const orderBuildStepService = inject(PROVIDER_CONSTANTS.orderBuildStepService) as OrderBuildStepService
    const orderPartService = inject(PROVIDER_CONSTANTS.orderPartService) as OrderPartService
    const orderService = inject(PROVIDER_CONSTANTS.orderService) as OrderService
    const orderServiceService = inject(PROVIDER_CONSTANTS.orderServiceService) as OrderServiceService
    const stateService = inject(PROVIDER_CONSTANTS.stateService) as StateService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

   return {
      buildTypeService,
      noteService,
      orderBuildStepService,
      orderPartService,
      orderService,
      orderServiceService,
      stateService,
      toast
    }
  },
  data() {
    let noteToEdit: Note|undefined
    let order: OrderDetails|undefined
    let orderPartNoteToEdit: Note|undefined
    let orderServiceNoteToEdit: Note|undefined
    let scheduledDate: Date|undefined
    let unchangedOrder: OrderDetails|undefined

    return {
      addingNote: false,
      addingPayment: false,
      buildTypeDropdownOptions: [] as BuildTypeDropdownOption[],
      creatingInvoice: false,
      creatingQuote: false,
      loading: true,
      newNoteText: '',
      newPaymentAmount: 0,
      noteToEdit,
      order,
      orderPartId: 0,
      orderPartNoteToEdit,
      orderServiceId: 0,
      orderServiceNoteToEdit,
      saving: false,
      scheduledDate,
      showAddPartModal: false,
      showAddServiceModal: false,
      showMenu: false,
      showNoteModal: false,
      showOrderPartNoteModal: false,
      showOrderServiceNoteModal: false,
      stateDropdownOptions: [] as DropdownOptionResponse<number>[],
      unchangedOrder,
      updatingInvoice: false
    }
  },
  computed: {
    addNoteEnabled(): boolean {
      return this.newNoteText.trim() !== ''
    },
    addPaymentEnabled(): boolean {
      return this.newPaymentAmount > 0
    },
    cartridgeRequired(): boolean {
      const index = this.buildTypeDropdownOptions.findIndex(b => b.id === this.order?.buildTypeId)
      if (index === -1)
        return false

      return this.buildTypeDropdownOptions[index].cartridgeRequired
    },
    isDirty(): boolean {
      const a = this.order
      const b = this.unchangedOrder

      let validCartridgeValue = true
      if (this.cartridgeRequired && !a?.cartridgeId)
        validCartridgeValue = false

      return (a?.cartridgeId !== b?.cartridgeId ||
             a?.customerId !== b?.customerId ||
             a?.onHold !== b?.onHold ||
             a?.scheduledDate !== b?.scheduledDate ||
             a?.buildTypeId !== b?.buildTypeId ||
             a?.partsDiscountPercentage !== b?.partsDiscountPercentage ||
             a?.servicesDiscountPercentage !== b?.servicesDiscountPercentage ||
             a?.customerSuppliedParts !== b?.customerSuppliedParts) && (a?.customerId !== undefined && validCartridgeValue)
    },
    stateDisplayValue(): string|undefined {
      if (this.order && this.order.customer.stateId) {
        const stateDropdownOption = this.stateDropdownOptions.find(o => o.id == this.order?.customer.stateId)
        return stateDropdownOption?.name
      }

      return undefined
    },
    titleText(): string {
      return `Order #${this.id}`
    }
  },
  mounted() {
    Promise.all([this.getBuildTypes(), this.getStates()]).then(() => {
      this.getOrder()
    })
  },
  methods: {
    closeMenu(): void {
      this.showMenu = false
    },
    getBuildTypes(): Promise<void> {
      return this.buildTypeService.getBuildTypeDropdownOptions().then(dropdownOptionsResponse => {
        this.buildTypeDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the build types. Try reloading the page.')
        }
      })
    },
    getOrder(): Promise<void> {
      return this.orderService.getOrder(this.order ? this.order.id : this.id).then(order => {
        this.order = order
        this.unchangedOrder = JSON.parse(JSON.stringify(order))
        this.updatePageTitle()
        this.scheduledDate = order.scheduledDate ? moment.utc(order.scheduledDate).local().toDate() : undefined 
        this.loading = false
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the order. Try reloading the page.')
        }
      })
    },
    getStates(): Promise<void> {
      return this.stateService.getStateDropdownOptions().then(dropdownOptionsResponse => {
        this.stateDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the states. Try reloading the page.')
        }
      })
    },
    onAddNoteClicked(): void {
      this.addingNote = true
      const noteMessage = new NoteMessage(this.newNoteText, false)
      this.orderService.createOrderNote(this.id, noteMessage).then(() => {
        this.getOrder().then(() => {
          this.newNoteText = ''
          this.addingNote = false
          this.toast.success('Note added successfully.')
        })
      }).catch(error => {
        this.addingNote = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to create the note. Please try again.')
        }
      })
    },
    onAddOrderPartNoteClicked(orderPartId: number): void {
      this.orderPartId = orderPartId
      this.orderPartNoteToEdit = new Note(0)
      this.showOrderPartNoteModal = true
    },
    onAddPartClicked(): void {
      this.showAddPartModal = true
    },
    onAddPaymentClicked(): void {
      this.addingPayment = true
      const orderPaymentMessage = new OrderPaymentMessage(this.newPaymentAmount)
      this.orderService.createOrderPayment(this.id, orderPaymentMessage).then(() => {
        this.getOrder().then(() => {
          this.newPaymentAmount = 0
          this.addingPayment = false
          this.toast.success('Payment added successfully.')
        })
      }).catch(error => {
        this.addingPayment = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to add the payment. Please try again.')
        }
      })
    },
    onAddServiceClicked(): void {
      this.showAddServiceModal = true
    },
    onAddServiceNoteClicked(orderServiceId: number): void {
      this.orderServiceId = orderServiceId
      this.orderServiceNoteToEdit = new Note(0)
      this.showOrderServiceNoteModal = true
    },
    onCreateInvoiceClicked(): void {
      this.creatingInvoice = true

      this.orderService.createOrderInvoice(this.id).then(() => {
        this.getOrder().then(() => {
          this.toast.success('Invoice created successfully.')
          this.creatingInvoice = false
        })
      }).catch(error => {
        this.creatingInvoice = false
        
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to create the invoice. Please try again.')
        }
      })
    },
    onDeleteNote(note: Note): void {
      this.noteService.deleteNote(note.id).then(() => {
        this.getOrder().then(() => {
          this.toast.success('The note was deleted successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the note. Please try again.')
        }
      })
    },
    onDuplicateClicked(): void {
      this.creatingQuote = true

      this.orderService.createQuoteFromOrder(this.id).then(objectCreatedResponse => {
        this.toast.success('A new quote was created from the existing order successfully.')
        this.$router.push('/quote/' + objectCreatedResponse.id)
      }).catch(error => {
        this.creatingQuote = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to duplicate the order as a new quote. Please try again.')
        }
      })
    },
    onEditCustomerClicked(): void {
      if (!this.order || !this.order.customerId) return

      const routeData = this.$router.resolve('/customer/' + this.order.customerId)
      window.open(routeData.href, '_blank')
    },
    onEditNote(note: Note): void {
      this.noteToEdit = note
      this.showNoteModal = true
    },
    onEditOrderPartNote(note: Note): void {
      this.orderPartNoteToEdit = note
      this.showOrderPartNoteModal = true
    },
    onEditOrderServiceNote(note: Note): void {
      this.orderServiceNoteToEdit = note
      this.showOrderServiceNoteModal = true
    },
    onOrderBuildStepStatusChanged(orderBuildStepId: number, buildStepStatus: number): void {
      const updateOrderBuildStepMessage = new UpdateOrderBuildStepMessage(buildStepStatus)
      this.orderBuildStepService.updateOrderBuildStep(orderBuildStepId, updateOrderBuildStepMessage).then(() => {
        this.getOrder().then(() => {
          this.toast.success('Build step status updated successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the build step status. Please try again.')
        }
      })
    },
    onOrderPartChanged(orderPartId: number, action: number|undefined, price: number, purchaseOrderItemId: number|undefined): void {
      const updateOrderPartMessage = new UpdateOrderPartMessage(action, price, purchaseOrderItemId)
      this.orderPartService.updateOrderPart(orderPartId, updateOrderPartMessage).then(() => {
        this.getOrder().then(() => {
          this.toast.success('The part was updated successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the part. Please try again.')
        }
      })
    },
    onOrderPartsDisplayOrderChanged(): void {
      if (!this.order) return

      const updateOrderPartsMessage = new UpdateOrderPartsMessage(this.order.orderParts.map(o => o.id))
      this.orderPartService.updateOrderParts(updateOrderPartsMessage).then(() => {
        this.getOrder().then(() => {
          this.toast.success('The part was updated successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the part. Please try again.')
        }
      })
    },
    onOrderServiceChanged(orderService: OrderServiceDetails): void {
      const updateOrderServiceMessage = new UpdateOrderServiceMessage(orderService.completed, orderService.price)
      this.orderServiceService.updateOrderService(orderService.id, updateOrderServiceMessage).then(() => {
        this.getOrder().then(() => {
          this.toast.success('The service was updated successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the service. Please try again.')
        }
      })
    },
    onOrderServicesDisplayOrderChanged(): void {
      if (!this.order) return

      const updateOrderServicesMessage = new UpdateOrderServicesMessage(this.order.orderServices.map(o => o.id))
      this.orderServiceService.updateOrderServices(updateOrderServicesMessage).then(() => {
        this.getOrder().then(() => {
          this.toast.success('The service was updated successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the service. Please try again.')
        }
      })
    },
    onRemoveOrderPartClicked(id: number): void {
      if (!this.order) return

      this.orderPartService.deleteOrderPart(id).then(() => {
        this.getOrder().then(() => {
          this.toast.success('The part was removed from the order successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to remove the part from the order. Please try again.')
        }
      })
    },
    onRemoveOrderServiceClicked(id: number): void {
      if (!this.order) return

      this.orderServiceService.deleteOrderService(id).then(() => {
        this.getOrder().then(() => {
          this.toast.success('The service was removed from the order successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to remove the service from the order. Please try again.')
        }
      })
    },
    onSaveOrderClicked(): void {
      if (!this.order || !this.order.customerId) return
      
      this.saving = true

      const utcScheduledDate = this.scheduledDate ? moment.utc(this.scheduledDate).format() : undefined
      const updateOrderMessage = new UpdateOrderMessage(this.order.buildTypeId, this.order.cartridgeId, this.order.customerSuppliedParts, this.order.onHold, this.order.partsDiscountPercentage, utcScheduledDate, this.order.servicesDiscountPercentage)
      this.orderService.updateOrder(this.id, updateOrderMessage).then(() => {
        this.getOrder().then(() => {
          this.toast.success('The order was updated successfully.')
          this.saving = false
        })
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the order. Please try again.')
        }
      })
    },
    onScheduledDateChanged(): void {
      const element = document.getElementById('scheduled-date')
      element?.blur()

      if (!this.order) return

      this.order.scheduledDate = this.scheduledDate ? moment.utc(this.scheduledDate).format() : undefined
    },
    onUpdateInvoiceClicked(): void {
      this.updatingInvoice = true

      this.orderService.updateOrderInvoice(this.id).then(() => {
        this.getOrder().then(() => {
          this.toast.success('Invoice updated successfully.')
          this.updatingInvoice = false
        })
      }).catch(error => {
        this.updatingInvoice = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the invoice. Please try again.')
        }
      })
    },
    orderPartIdsFromOrderPart(orderPart: OrderPartDetails): number[] {
      if (!this.order) return []

      const orderParts = this.order.orderParts.filter(op => op.inventoryItemId === orderPart.inventoryItemId)
      return orderParts.map(op => op.id)
    },
    orderPartStatusToDisplayValue(status: number): string {
      switch(status) {
        case 1: return 'Needs Allocated'
        case 2: return 'Purchase Order Created'
        case 3: return 'Purchase Order Submitted'
        case 4: return 'Purchase Order Verified'
        case 5: return 'Pulled From Inventory'
        default: return ''
      }
    },
    statusText(orderBuildStep: OrderBuildStepDetails): string {
      switch(orderBuildStep.processStatus) {
        case 1: return 'Pending'
        case 2: return 'In Progress'
        case 3: return `Completed by ${orderBuildStep.completedByUser?.firstName} ${orderBuildStep.completedByUser?.lastName}`
        default: return ''
      }
    },
    updatePageTitle(): void {
      if (this.order)
        document.title = `Order #${this.order.id} - Short Action Customs, LLC`
    }
  }
})
</script>

<style lang="scss">
.order-details .date-picker {
  @include input-text-field;
  cursor: pointer;
  margin-bottom: 15px;
}
</style>

<style scoped lang="scss">
.order-details {
  background-color: $Color-fill-gray;
  position: relative;
  min-height: calc(100vh - 94px);

  @include desktop {
    min-height: calc(100vh - 130px);
  }

  > .loading-spinner {
    position: absolute;
    top: 50%;
    right: 50%;
    transform: translate(50%, 50%);
  }

  > .order-details-content {
    display: grid;
    column-gap: 20px;
    grid-template-areas: 
      "header"
      "left"
      "right";
      grid-template-columns: 100%;

    @include desktop {
      grid-template-columns: 340px minmax(0, 1fr);
      grid-template-areas:
        "header header"
        "left right";
    }

    > .order-details-header {
      grid-area: header;
      padding: 15px 15px 10px 15px;
      margin-bottom: 15px;
      border-bottom: 1px solid $Color-border-gray;
      display: flex;
      flex-direction: column;
      position: sticky;
      background-color: $Color-fill-gray;
      z-index: 100;
      top: 94px;

      @include desktop {
        flex-direction: row;
        top: 130px;
      }

      > .title-text {
        line-height: 37px;
        font-size: 24px;
        font-weight: bold;
      }

      > .actions {
        position: relative;
        display: flex;
        margin-left: auto;

        > .duplicate {
          margin-left: auto;
        }

        > .save {
          margin-left: 15px;
        }
      }
    }

    > .left-column {
      grid-area: left;
      padding-left: 15px;
      padding-right: 15px;

      @include desktop {
        padding-right: 0;
      }
    }

    > .right-column {
      grid-area: right;
      padding-left: 15px;
      padding-right: 15px;

      @include desktop {
        padding-left: 0;
      }
    }

    .card {
      background-color: $Color-white;
      margin-bottom: 15px;
      @include element-shadow;

      > .header {
        background-color: $Color-fill-black;
        color: $Color-white;
        padding: 15px;
        position: relative;

        > .add {
          fill: $Color-white;
          position: absolute;
          right: 15px;
          top: 15px;
          width: 18px;
          height: 18px;
          cursor: pointer;
        }
      }

      > .content {
        padding: 15px;

        &.flexed {
          display: flex;
        }

        > .input {
          margin-bottom: 10px;
        }

        > .input-label {
          @include input-text-field-label;
        }

        > .total {
          margin-bottom: 15px;
          font-weight: 600;
          color: $Color-font-high;
          font-size: 1.3rem;
        }

        > .approval-info {
          margin-top: 15px;
          font-size: 12px;
          line-height: 18px;
          font-weight: normal;
          color: $Color-font-medium;
        }

        > .add {
          @include positive-button;
          margin-top: 10px;
          margin-bottom: 15px;
        }

        > .view-invoice {
          margin-right: auto;
          color: $Color-sac-orange;
          text-decoration: none;
          font-size: 15px;
          line-height: 36px;
        }

        > .payment {
          padding: 10px 0;
          border-bottom: 1px solid $Color-border-gray;

          > .price {
            font-size: 16px;
            font-weight: 600;
            color: $Color-font-high;
          }

          > .received-by {
            font-size: 12px;
            color: $Color-font-medium;
          }
        }

        > .customer-tile {
          position: relative;
          background-color: $Color-fill-gray;
          padding: 15px;
          border-radius: 6px;
          display: flex;

          > .customer-tile-content {
            flex: 0 0 90%;
            display: flex;
            flex-direction: column;
            line-height: 24px;

            > .name {
              color: $Color-font-high;
              font-weight: 600;
            }

            > .address {
              color: $Color-font-medium;
              font-size: 15px;
            }

            > a {
              color: $Color-font-medium;
              text-decoration: none;
              font-style: italic;
              font-size: 15px;
            }
          }

          > svg {
            flex: 0 0 10%;
            margin: auto 10px;
          }

          > .menu {
            @include menu;
          }
        }

        > .overflow-wrapper {
          overflow-y: auto;
          @include sac-table;

          td.description {
            white-space: break-spaces;
          }

          td.services {
            padding-top: 15px;
            min-width: 450px;

            > .service {
              margin-bottom: 15px;

              &:last-of-type {
                margin-bottom: 0;
              }
            }
          }

          td.notes {
            min-width: 300px;
          }

          .complete {
            @include flat-button;
          }
        }

        .add-comment {
          margin-right: 10px;
        }
      }

      > .footer {
        text-align: right;
        background-color: $Color-fill-black;
        color: $Color-white;
        padding: 15px;
      }
    }
  }
}

.slide-up {
  @include modal-animation;
}
</style>
